/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    FormGroup,
    makeStyles,
    Radio,
    RadioGroup,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ActionButton, ActionLink } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import { MimeTypes, useFileContext } from '@klp/file';
import {
    FormButton,
    FormByggKundeSelector,
    FormCheckbox,
    FormDatePicker,
    FormDropdown,
    FormEditor,
    FormFiles,
    FormInput,
    FormValidator,
    useFormContext,
} from '@klp/form';

import { messageApiRoutes } from '../api';
import { MessageCategory, MessageStatus } from '../classes';
import type { IMessageInput, Message } from '../classes';

type MessageFormMode = 'add' | 'edit';

interface IMessageFormContentProps {
    mode: MessageFormMode;
    message: Message;
    isLoading: boolean;
    onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
        width: '100%',
    },
    fileUploader: {
        width: '100%',
        maxWidth: '600px',
    },
}));

const isSelectedDateToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
};

export const MessageFormContent: React.FC<IMessageFormContentProps> = ({
    mode,
    message,
    isLoading,
    onCancel,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const context = useFormContext<IMessageInput>();
    const { clearQueues } = useFileContext();

    const [notifyByEMailSelected, setNotifyByEMailSelected] = useState(
        message.notifyByEMail,
    );
    const [emailDialogVisible, setEmailDialogVisible] = useState(false);
    const [isPublishDateToday, setIsPublishDateToday] = useState(
        message.publishAt
            ? isSelectedDateToday(new Date(message.publishAt))
            : false,
    );
    const [sendImmediatelyValue, setSendImmediatelyValue] = useState(
        message.sendImmediately ? 'true' : 'false',
    );

    const notificationSentAt = message.notificationSentAt;

    // Scroll to top on mount and clear file queues on unmount
    useEffect(() => {
        window.scrollTo(0, 0);
        return () => {
            clearQueues();
        };
    }, [clearQueues]);

    const emailDialogShow = () => {
        setEmailDialogVisible(true);
    };

    const emailDialogClose = () => {
        setEmailDialogVisible(false);
    };

    const notifyByEMailSelect = (selected: boolean) => {
        setNotifyByEMailSelected(selected);
    };

    const handlePublishDateChange = (selectedDate: Date) => {
        const isSameDay = isSelectedDateToday(selectedDate);
        setIsPublishDateToday(isSameDay);
    };

    const handleSaveAsDraft = () => {
        if (context.formDisabled || context.formHasErrors) {
            return;
        }
        context.submit?.();
    };

    const handleEmailConfirm = () => {
        emailDialogClose();
        context.setValue!('status', MessageStatus.Published);
        context.submit?.();
    };

    const {
        id,
        title,
        content,
        category,
        publishAt,
        expiresAt,
        mottakereList,
        notifyByEMail,
        attachments,
        isPinned,
    } = message;

    return (
        <div className={classes.formContainer}>
            <FormInput
                inputProps={{ maxLength: 255 }}
                name="title"
                title={t('message.title.name')}
                validators={[
                    new FormValidator(
                        FormValidator.validations.required,
                        t('validator.required'),
                    ),
                ]}
                value={title}
                helperText={t('message.title.helperText')}
                required={true}
            />

            <FormDropdown
                name="category"
                title={t('message.category.name')}
                value={category}
                items={Object.keys(MessageCategory).map((item) => {
                    return {
                        label: t(`message.categoryType.${item}`),
                        value: item,
                    };
                })}
                validators={[
                    new FormValidator(
                        FormValidator.validations.required,
                        t('validator.required'),
                    ),
                ]}
                helperText={t('message.category.helperText')}
                required={true}
            />

            <FormEditor
                name="content"
                title={t('message.content.name')}
                value={content}
                helperText={t('message.content.helperText')}
            />
            <div className={classes.fileUploader}>
                <FormFiles
                    name="messageImagesUploader"
                    title={t('message.messageImages.name')}
                    value={attachments}
                    mimeTypes={MimeTypes.image}
                    path={messageApiRoutes.messageFile(id)}
                    helperText={t('message.messageImages.helperText')}
                    accept={MimeTypes.image}
                    queueName="messageImages"
                />
            </div>
            <div className={classes.fileUploader}>
                <FormFiles
                    name="messageFilesUploader"
                    title={t('message.messageDocuments.name')}
                    value={attachments}
                    mimeTypes={MimeTypes.document}
                    path={messageApiRoutes.messageFile(id)}
                    helperText={t('message.messageDocuments.helperText')}
                    accept={MimeTypes.document}
                    queueName="messageAttachments"
                />
            </div>

            <FormDatePicker
                name="publishAt"
                onChange={handlePublishDateChange}
                title={t('message.publishAt.name')}
                value={publishAt ?? null}
                helperText={t('message.publishAt.helperText')}
                disablePast={true}
            />

            <FormDatePicker
                name="expiresAt"
                title={t('message.expiresAt.name')}
                value={expiresAt ?? null}
                helperText={t('message.expiresAt.helperText')}
                disablePast={true}
            />

            <FormCheckbox
                name="isPinned"
                title={t('message.isPinned.name')}
                value={isPinned}
                helperText={t('message.isPinned.helperText')}
            />

            <FormByggKundeSelector
                value={mottakereList}
                name="mottakereList"
                title={t('buildingClientChooser.recipients.select')}
                primarySelect="kunde"
                secondarySelect="bygg"
                validators={[
                    new FormValidator(
                        FormValidator.validations.required,
                        t('validator.required'),
                    ),
                ]}
            />

            {!notificationSentAt ? (
                <FormGroup>
                    <FormCheckbox
                        name="notifyByEMail"
                        title={t('message.channel.email')}
                        value={notifyByEMail}
                        onChange={(selected) => notifyByEMailSelect(selected)}
                    />
                    {notifyByEMailSelected && (
                        <div>
                            {isPublishDateToday ? (
                                <RadioGroup
                                    aria-label="send immediately"
                                    name="sendImmediately"
                                    value={sendImmediatelyValue}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setSendImmediatelyValue(newValue);
                                        context.setValue!(
                                            'sendImmediately',
                                            newValue === 'true',
                                        );
                                    }}
                                >
                                    <FormControlLabel
                                        value="false"
                                        control={<Radio />}
                                        label={t('message.channel.sendTonight')}
                                    />
                                    <FormControlLabel
                                        value="true"
                                        control={<Radio />}
                                        label={t(
                                            'message.channel.sendImmediately',
                                        )}
                                    />
                                </RadioGroup>
                            ) : (
                                <Alert severity="info" color="info">
                                    {t('message.channel.infoText')}
                                </Alert>
                            )}
                        </div>
                    )}
                </FormGroup>
            ) : (
                <Alert severity="info" color="info">
                    {t('message.channel.notificationSent', {
                        timeSent: notificationSentAt?.toLocaleString(),
                    })}
                </Alert>
            )}

            <ButtonsContainer justifyContent="space-between">
                <ActionButton
                    actionType="back"
                    onClick={onCancel}
                    translation="general.goBack"
                />

                {mode === 'add' && notifyByEMailSelected && (
                    <FormButton
                        actionType="forward"
                        disableOnError={true}
                        translation="general.publish"
                        onClick={emailDialogShow}
                        isLoading={isLoading}
                    />
                )}

                {((mode === 'add' && !notifyByEMailSelected) ||
                    mode === 'edit') && (
                    <FormButton
                        actionType="submit"
                        disableOnError={true}
                        translation="general.publish"
                        onClick={() => {
                            context.setValue!(
                                'status',
                                MessageStatus.Published,
                            );
                        }}
                        isLoading={isLoading}
                    />
                )}
            </ButtonsContainer>

            {mode === 'add' && (
                <ButtonsContainer justifyContent="flex-end">
                    <ActionLink
                        actionType="link"
                        translation="general.saveAsDraft"
                        marginTop="compact"
                        onClick={handleSaveAsDraft}
                        klpTypographyVariant="small"
                        disabled={context.formDisabled || context.formHasErrors}
                    />
                </ButtonsContainer>
            )}

            <Dialog open={emailDialogVisible} onClose={emailDialogClose}>
                <DialogContent>
                    <DialogContentText>
                        {t('message.channel.confirmEmail')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        onClick={emailDialogClose}
                        translation="general.decline"
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        translation="message.channel.yes"
                        onClick={handleEmailConfirm}
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
};
